import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useAuthHeader } from "react-auth-kit";
import Accordion from "react-bootstrap/Accordion";
import { Container } from "react-bootstrap";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare, faTrash, faCloudArrowUp, faFileCirclePlus } from "@fortawesome/free-solid-svg-icons";
import sanitizeHtml from "sanitize-html";
import "./edit.css";

const backend = process.env.REACT_APP_BACKEND_API;

const EDITOR_MODULES = {
  toolbar: [["bold", "italic", "underline"]],
};

const EDITOR_FORMATS = ["bold", "italic", "underline"];

function ModelItem({ model, type, token, onRefresh, onOpen }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [header, setHeader] = useState(model.name);
  const [data, setData] = useState(model.data);

  useEffect(() => {
    if (!editing) {
      setHeader(model.name);
      setData(model.data);
    }
  }, [model.name, model.data, editing]);

  const handleEditClick = () => {
    onOpen(model._id);
    setEditing(true);
  };

  const handleSaveClick = async () => {
    if (!header.trim()) {
      window.alert("Informe um nome para este item.");
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${backend}/${type}/${model._id}`,
        { name: header.trim(), data },
        { headers: { Authorization: token } }
      );
      setEditing(false);
      await onRefresh();
    } catch (error) {
      console.error("Erro ao salvar item:", error);
      window.alert("Não foi possível salvar as alterações. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Excluir “${model.name}”? Esta ação não pode ser desfeita.`)) {
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`${backend}/${type}/${model._id}`, {
        headers: { Authorization: token },
      });
      await onRefresh();
    } catch (error) {
      console.error("Erro ao excluir item:", error);
      window.alert("Não foi possível excluir este item. Tente novamente.");
    } finally {
      setDeleting(false);
    }
  };

  const isBusy = saving || deleting;

  return (
    <Accordion.Item className="db-editor-item" eventKey={model._id}>
      <Accordion.Header>
        {editing ? (
          <input
            className="db-editor-title-input"
            type="text"
            value={header}
            aria-label={`Nome de ${model.name}`}
            onClick={(event) => event.stopPropagation()}
            onFocus={() => onOpen(model._id)}
            onChange={(event) => setHeader(event.target.value)}
          />
        ) : (
          <span className="db-editor-item-title">{header}</span>
        )}
      </Accordion.Header>
      <Accordion.Body>
        {editing ? (
          <>
            <div className="db-editor-item-actions">
              <button type="button" className="db-editor-action db-editor-save" onClick={handleSaveClick} disabled={isBusy}>
                <FontAwesomeIcon icon={faCloudArrowUp} /> {saving ? "Salvando..." : "Salvar alterações"}
              </button>
              <button type="button" className="db-editor-action db-editor-delete" onClick={handleDelete} disabled={isBusy}>
                <FontAwesomeIcon icon={faTrash} /> {deleting ? "Excluindo..." : "Excluir"}
              </button>
            </div>
            <ReactQuill
              className="db-editor-quill"
              value={data}
              onChange={setData}
              modules={EDITOR_MODULES}
              formats={EDITOR_FORMATS}
            />
          </>
        ) : (
          <>
            <div className="db-editor-item-actions">
              <button type="button" className="db-editor-action db-editor-edit" onClick={handleEditClick}>
                <FontAwesomeIcon icon={faPenToSquare} /> Editar
              </button>
              <button type="button" className="db-editor-action db-editor-delete" onClick={handleDelete} disabled={deleting}>
                <FontAwesomeIcon icon={faTrash} /> {deleting ? "Excluindo..." : "Excluir"}
              </button>
            </div>
            <div className="db-editor-preview" dangerouslySetInnerHTML={{ __html: sanitizeHtml(model.data) }} />
          </>
        )}
      </Accordion.Body>
    </Accordion.Item>
  );
}

export default function DBEditor({ type }) {
  const authHeader = useAuthHeader();
  const token = authHeader();
  const [newModel, setNewModel] = useState(false);
  const [newHeader, setNewHeader] = useState("");
  const [newData, setNewData] = useState("");
  const [newSaving, setNewSaving] = useState(false);
  const [modelList, setModelList] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      const response = await axios.get(`${backend}/${type}`, {
        headers: { Authorization: token },
      });
      setModelList(response.data);
    } catch (error) {
      console.error("Erro ao carregar itens:", error);
      setLoadError("Não foi possível carregar os itens. Verifique sua conexão e tente novamente.");
    } finally {
      setIsLoading(false);
    }
  }, [token, type]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleNewClick = () => {
    if (newModel) {
      handleNewDelete();
      return;
    }
    setNewModel(true);
    setActiveKey("new");
  };

  const handleNewSaveClick = async () => {
    if (!newHeader.trim()) {
      window.alert("Informe um nome para o novo item.");
      return;
    }

    setNewSaving(true);
    try {
      await axios.post(
        `${backend}/${type}`,
        { name: newHeader.trim(), data: newData },
        { headers: { Authorization: token } }
      );
      handleNewDelete();
      setActiveKey(null);
      await fetchData();
    } catch (error) {
      console.error("Erro ao criar item:", error);
      window.alert("Não foi possível criar este item. Tente novamente.");
    } finally {
      setNewSaving(false);
    }
  };

  const handleNewDelete = () => {
    setNewModel(false);
    setNewHeader("");
    setNewData("");
  };

  const pageTitle = type === "models" ? "Prompts" : "Modelos";
  const pageDescription = type === "models"
    ? "Instruções reutilizáveis para organizar e transformar prontuários."
    : "Estruturas prontas para começar um novo documento.";

  return (
    <main className="db-editor-page">
      <Container className="db-editor-shell">
        <header className="db-editor-heading">
          <div>
            <span className="db-editor-eyebrow">Biblioteca HealthPad</span>
            <h1>{pageTitle}</h1>
            <p>{pageDescription}</p>
          </div>
          <button type="button" onClick={handleNewClick} className="db-editor-new-button" aria-expanded={newModel}>
            <FontAwesomeIcon icon={faFileCirclePlus} /> {newModel ? "Cancelar novo" : "Criar novo"}
          </button>
        </header>

        <section className="db-editor-card" aria-live="polite">
          {loadError && <div className="db-editor-feedback" role="alert">{loadError}</div>}
          {isLoading ? (
            <div className="db-editor-loading">Carregando {pageTitle.toLowerCase()}...</div>
          ) : (
            <Accordion
              className="db-editor-accordion"
              activeKey={activeKey}
              onSelect={(key) => setActiveKey(key)}
            >
              {modelList.map((model) => (
                <ModelItem
                  model={model}
                  type={type}
                  token={token}
                  onRefresh={fetchData}
                  onOpen={setActiveKey}
                  key={model._id}
                />
              ))}

              {newModel && (
                <Accordion.Item className="db-editor-item db-editor-new-item" eventKey="new">
                  <Accordion.Header>
                    <input
                      className="db-editor-title-input"
                      type="text"
                      value={newHeader}
                      placeholder={`Nome do novo ${type === "models" ? "prompt" : "modelo"}`}
                      aria-label="Nome do novo item"
                      onClick={(event) => event.stopPropagation()}
                      onFocus={() => setActiveKey("new")}
                      onChange={(event) => setNewHeader(event.target.value)}
                    />
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className="db-editor-item-actions">
                      <button type="button" className="db-editor-action db-editor-save" onClick={handleNewSaveClick} disabled={newSaving}>
                        <FontAwesomeIcon icon={faCloudArrowUp} /> {newSaving ? "Salvando..." : "Salvar novo"}
                      </button>
                      <button type="button" className="db-editor-action db-editor-delete" onClick={handleNewDelete} disabled={newSaving}>
                        <FontAwesomeIcon icon={faTrash} /> Cancelar
                      </button>
                    </div>
                    <ReactQuill
                      className="db-editor-quill"
                      value={newData}
                      onChange={setNewData}
                      modules={EDITOR_MODULES}
                      formats={EDITOR_FORMATS}
                    />
                  </Accordion.Body>
                </Accordion.Item>
              )}

              {!newModel && modelList.length === 0 && (
                <div className="db-editor-empty">Nenhum item cadastrado. Clique em “Criar novo” para começar.</div>
              )}
            </Accordion>
          )}
        </section>
      </Container>
    </main>
  );
}
